import { useMemo } from "react"
import { Box, IconButton, Stack, StackProps, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material"

import { useTenant } from "features/tenant/context"
import { useSearch } from "features/search/context"

import {
  SearchIcon,
  CloseIcon,
  DownloadIcon,
  PreviousIcon,
  NextIcon,
} from 'shared/icons'
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material'

import { KojinAvatar } from "features/kojin/components/avatar"
import { KojinFlags } from "features/kojin/components/kojinFlags"
import { AuthorFlags } from "features/kojin/components/authorFlags"

import { useKojinPreview } from "../container"

const googleSearchUrl = 'https://www.google.com/search?q='

type KojinHeaderProps = {
	container: ReturnType<typeof useKojinPreview>
	tabName: string
} & StackProps
export const KojinHeader = ({
	container: {
		kojin, author, annotate,
		hasNext, hasPrev,
		openNext, openPrev,
		onKojinDownloadOpen,
		onClose
	},
	tabName,
	...others
}: KojinHeaderProps) => {
	const search = useSearch()
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down("sm"))
	const tenant = useTenant()

  const searchWord = useMemo<string | null>(
		() => (
			kojin?.isiName ? `"${kojin.isiName}"` :
			author?.isiName ? (search ? `"${author.isiName}" "${search.title}"` : `"${author.isiName}"`) :
			null
		),
		[search, kojin, author]
	)
	return (
		<Stack width="100%" boxSizing="border-box" direction="row" alignItems="flex-start" spacing={1} {...others}>
			<Stack flexGrow={1} flexShrink={0} direction="row" spacing={1} alignItems="center">
				<Box padding={1}>
					<KojinAvatar
						kojin={kojin || undefined}
						annotate={annotate || undefined}
						size="medium"
					/>
				</Box>
				<Stack
					direction={isSmallMedia ? "column" : "row"}
					spacing={isSmallMedia ? 0.5 : 1}
					flexGrow={1}
				>
					<Stack direction="column" flexGrow={1}>
						{kojin ? (
							<>
								<Typography whiteSpace="nowrap" variant="body2" color="text.primary" component="div" lineHeight={1}>{kojin.isiNameKana}</Typography>
								<Typography whiteSpace="nowrap" variant="h5" color="text.primary" component="div">{kojin.isiName}</Typography>
							</>
						) : author ? (
							<Typography whiteSpace="nowrap" variant="h5" color="text.primary" component="div">{author.isiName}</Typography>
						) : (
							<Typography whiteSpace="nowrap" variant="h5" color="text.secondary" component="div">loading..</Typography>
						)}
					</Stack>

					{isSmallMedia && (
						kojin ? <KojinFlags kojin={kojin}/> : author ? <AuthorFlags author={author}/> : <></>
					)}
				</Stack>
			</Stack>

			<Stack direction="row" flexWrap="wrap-reverse" justifyContent="flex-end" useFlexGap>
				{isSmallMedia === false && (
					kojin ? <KojinFlags paddingX={1} kojin={kojin}/> : author ? <AuthorFlags paddingX={1} author={author}/> : <></>
				)}

				{searchWord ? (
					<Tooltip title="医師名で検索">
						<IconButton
							href={`${googleSearchUrl}${searchWord}`}
							target="_blank"
						>
							<SearchIcon />
						</IconButton>
					</Tooltip>
				) : (
					<IconButton disabled>
						<SearchIcon />
					</IconButton>
				)}

				{kojin ? (
					<Tooltip title="ダウンロード">
						<IconButton disabled={kojin === null} onClick={(e) => {
							e.preventDefault()
							onKojinDownloadOpen()
						}}>
							<DownloadIcon />
						</IconButton>
					</Tooltip>
				) : (
					<IconButton disabled>
						<DownloadIcon />
					</IconButton>
				)}

				{(hasPrev || hasNext) ? (
					<Stack direction="row">
						{hasPrev ? (
							<Tooltip title="前へ">
								<IconButton onClick={openPrev}><PreviousIcon /></IconButton>
							</Tooltip>
						) : (
							<IconButton disabled><PreviousIcon /></IconButton>
						)}
						{hasNext ? (
							<Tooltip title="次へ">
								<IconButton onClick={openNext}><NextIcon /></IconButton>
							</Tooltip>
						) : (
							<IconButton disabled><NextIcon /></IconButton>
						)}
					</Stack>
				) : <></>}

				{onClose && (
					<>
						<Tooltip title="別のウィンドウで開く">
							<IconButton
								href={
									search ? (
										kojin ? `/${tenant.domain}/search/${search.searchId}/kojin/${kojin.kojinCode}?page=${tabName}` :
										author ? `/${tenant.domain}/search/${search.searchId}/author/${author.authorId}?page=${tabName}` : '#'
									) : (
										kojin ? `/${tenant.domain}/kojin/${kojin.kojinCode}?page=${tabName}` : '#'
									)
								}
								disabled={kojin === null && author === null}
								target='_blank'
							>
								<OpenInNewIcon />
							</IconButton>
						</Tooltip>

						<Tooltip title="閉じる">
							<IconButton onClick={onClose}><CloseIcon /></IconButton>
						</Tooltip>
					</>
				)}
			</Stack>
		</Stack>
	)
}